import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { useTranslation } from 'react-i18next';

interface OutputItemMetadataLabelProps {
  /** Wire-format item type from the derived feature (e.g. "Signal", "Scalar", "Condition"). */
  outputItemType?: string;
  /**
   * Wire-format metadata map from the source derived feature. `undefined` means the feature has no
   * restriction (renders "Any"). Callers should only render this component for rows that have
   * opted in to the metadata annotation.
   */
  outputItemMetadata?: Record<string, string>;
}

const uomLabel = (uom: string | undefined, t: (key: string) => string): string => {
  if (uom === undefined) return t('TEMPLATES.ANY_UOM_LABEL');
  if (uom === '') return t('TEMPLATES.DIMENSIONLESS_UOM_LABEL');
  return uom;
};

// The model PUT wire input accepts SeeqNames.FormulaType.* names ("Signal", "Scalar", "Condition"),
// while the model GET response emits SeeqNames.Types.* names ("Signal", "ScalarItem", "Condition").
// Signal and Condition agree, but Scalar diverges to "ScalarItem" — accept both so labels render on
// both freshly compiled features and round-tripped models. "Measure" is a widening type that accepts
// either Signal or Scalar (backend OutputItemMetadata.Measure) and uses the Signal UOM key.
const buildText = (
  outputItemType: string | undefined,
  outputItemMetadata: Record<string, string>,
  t: (key: string) => string,
): string | undefined => {
  switch (outputItemType) {
    case SeeqNames.FormulaType.Condition:
    case SeeqNames.Types.Condition:
      return 'Condition';
    case SeeqNames.FormulaType.Signal:
    case SeeqNames.Types.Signal:
      return `Signal (${uomLabel(outputItemMetadata[SeeqNames.Properties.ValueUom], t)})`;
    case SeeqNames.FormulaType.Scalar:
    case SeeqNames.Types.Scalar:
      return `Scalar (${uomLabel(outputItemMetadata[SeeqNames.Properties.Uom], t)})`;
    case 'Measure':
      return `Measure (${uomLabel(outputItemMetadata[SeeqNames.Properties.ValueUom], t)})`;
    default:
      return undefined;
  }
};

/**
 * Inline annotation describing a model derived feature's output item restriction. Rendered with a
 * bold "Metadata:" prefix that matches the "Name:" prefix used by `FormulaItemTable` rows.
 *
 *  - `outputItemMetadata === undefined` → renders "Any" (no restriction).
 *  - `outputItemMetadata` is a map → renders the type plus the UOM annotation when applicable.
 *
 * Renders nothing only when metadata is present but the type is unrecognised (defensive fallback).
 */
export const OutputItemMetadataLabel = ({ outputItemType, outputItemMetadata }: OutputItemMetadataLabelProps) => {
  const { t } = useTranslation();

  const value =
    outputItemMetadata === undefined ? t('TEMPLATES.ANY_TYPE_LABEL') : buildText(outputItemType, outputItemMetadata, t);

  if (value === undefined) {
    return null;
  }

  return (
    <span data-testid="outputItemMetadataLabel">
      <span className="text-bold">{t('TEMPLATES.METADATA_LABEL')}:</span> {value}
    </span>
  );
};
